import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { GALLERY } from "../../data/content";
import { Reveal } from "./Reveal";

export const Gallery = () => {
  const [index, setIndex] = useState(null);
  const open = index !== null;

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
  }, [open]);

  const close = () => setIndex(null);
  const prev = (e) => { e?.stopPropagation(); setIndex((i) => (i - 1 + GALLERY.length) % GALLERY.length); };
  const next = (e) => { e?.stopPropagation(); setIndex((i) => (i + 1) % GALLERY.length); };

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <section id="gallery" className="relative mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32 scroll-mt-20">
      <div className="mb-10">
        <Reveal><p className="text-[11px] uppercase tracking-[0.24em] text-gold font-semibold">Feast Your Eyes</p></Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-3 font-display text-4xl sm:text-6xl font-semibold tracking-tight text-cream">
            The gallery
          </h2>
        </Reveal>
      </div>

      <div className="columns-2 gap-4 sm:columns-3 [&>*]:mb-4">
        {GALLERY.map((g, i) => (
          <Reveal key={i} delay={(i % 6) * 0.04}>
            <button
              onClick={() => setIndex(i)}
              data-testid={`gallery-item-${i}`}
              className={`group relative block w-full overflow-hidden rounded-2xl border border-white/8 ${g.w === "tall" ? "aspect-[3/4]" : "aspect-square"}`}
            >
              <img
                src={g.src}
                alt={g.label}
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
              <span className="absolute bottom-3 left-3 translate-y-2 text-sm font-semibold text-cream opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
                {g.label}
              </span>
            </button>
          </Reveal>
        ))}
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[120] flex items-center justify-center bg-black/92 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
            data-testid="lightbox"
          >
            <button onClick={close} aria-label="Close" className="absolute right-5 top-5 z-10 h-11 w-11 grid place-items-center rounded-full border border-white/20 text-cream hover:border-gold/50 transition-colors">
              <X size={20} />
            </button>
            <button onClick={prev} aria-label="Previous" className="absolute left-3 sm:left-6 z-10 h-12 w-12 grid place-items-center rounded-full border border-white/15 text-cream hover:border-gold/50 transition-colors">
              <ChevronLeft size={22} />
            </button>
            <button onClick={next} aria-label="Next" className="absolute right-3 sm:right-6 z-10 h-12 w-12 grid place-items-center rounded-full border border-white/15 text-cream hover:border-gold/50 transition-colors">
              <ChevronRight size={22} />
            </button>
            <motion.figure
              key={index}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="relative max-h-[86vh] max-w-4xl"
              onClick={(e) => e.stopPropagation()}
            >
              <img src={GALLERY[index].src} alt={GALLERY[index].label} className="max-h-[86vh] w-auto rounded-2xl object-contain" />
              <figcaption className="mt-3 text-center text-sm text-cream/70">{GALLERY[index].label}</figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Gallery;
